/**
 * Erklæringskrav — kravtypen søkeren KRYSSER AV for, i stedet for å laste opp en
 * fil. I praksis kun sjøfartskortet (SID), der de fire erklæringene bærer
 * straffelovhenvisningen og skal ende opp i det arkiverte søknadsdokumentet.
 *
 * Avkryssingen lagres som en `ConfirmedQualification` PÅ SØKNADEN gjennom det
 * eksisterende endepunktet
 * `PUT/DELETE v1/applications/{id}/qualifications/{uri}/confirmation` — nøyaktig
 * samme flyt som den gamle kvalifikasjonssiden brukte. Kravmotoren leser aldri
 * disse: den svarer `Inconclusive` («vurderes utenfor motoren») for typen, og
 * det er avkryssingen som avgjør om kravet er oppfylt for søkeren.
 *
 * **Nøkkelen** som lagres er den samme som vedleggene bruker,
 * `certreq:{kode}#{requirementId}`. Backend lowercaser og unescaper URI-en før
 * den lagres (`QualificationConfirmationController.UnescapeAndLowerCase`), så
 * alt som leses tilbake må sammenliknes UTEN hensyn til store og små bokstaver.
 * Derfor holder modulen alle nøkler og id-er i små bokstaver.
 */

import { ConfirmedQualificationResult } from '@sdir/httpclient/lib/clients/aps/application';
import { CERTREQ_URI_PREFIX } from './requirementKeys';
import { baseRequirementType } from './requirementTypes';
import { RequirementResult } from './types';

/** Kravtypen slik den står i kravdokumentet fra og med skjema v3. */
export const DECLARATION_REQUIREMENT_TYPE = 'Declaration';

/**
 * Er dette et erklæringskrav?
 *
 * Går via `baseRequirementType` slik at eventuelle suffiksvarianter
 * (`Declaration_SID`) treffer på samme måte som for de andre kravtypene.
 */
export const isDeclarationRequirement = (requirementType?: string | null): boolean =>
  baseRequirementType(requirementType) === DECLARATION_REQUIREMENT_TYPE;

/**
 * Kravene søkeren har avgitt erklæring på, som kravenes id-er i SMÅ BOKSTAVER.
 *
 * Formen speiler `uploadedFiles` i søkervisningen: oppslaget skjer på kravets
 * `requirementId`, ikke på hele nøkkelen, fordi modellen bygges av kravtreet og
 * ikke kjenner sertifikatkoden på hver enkelt node. Små bokstaver er ikke en
 * forenkling — det er alt backend har igjen av nøkkelen.
 */
export const confirmedRequirementIds = (
  confirmations: ConfirmedQualificationResult[] | null | undefined,
  certificateCode?: string | null
): ReadonlySet<string> => {
  const ids = new Set<string>();
  if (!certificateCode) return ids;

  const prefix = `${CERTREQ_URI_PREFIX}:${certificateCode}#`.toLowerCase();

  (confirmations || []).forEach((confirmation) => {
    const uri = (confirmation.qualificationUri || '').toLowerCase();
    if (!uri.startsWith(prefix)) return;
    const requirementId = uri.slice(prefix.length);
    if (requirementId) ids.add(requirementId);
  });

  return ids;
};

/** Er kravet med denne id-en erklært? Sammenlikningen er case-ufølsom. */
export const isRequirementConfirmed = (
  confirmedIds: ReadonlySet<string>,
  requirementId?: string | null
): boolean => !!requirementId && confirmedIds.has(requirementId.toLowerCase());

/* ------------------------------------------------------------------ */
/* «Neste»-sperren                                                      */
/* ------------------------------------------------------------------ */

const isLeaf = (result: RequirementResult): boolean =>
  typeof result.requirementType === 'string' && result.requirementType.length > 0;

/**
 * Er erklæringene i kravtreet avgitt slik strukturen krever?
 *
 * Den gamle kvalifikasjonssiden sperret «Neste» til søkeren hadde et komplett
 * kvalifikasjonssett — og for SID betydde det i praksis at erklæringene i ÉN
 * alternativvei var huket av. Regelen her er den samme, men avgrenset til
 * erklæringene:
 *
 *   - Et erklæringskrav er tilfredsstilt når det er avgitt.
 *   - Alle ANDRE krav er **nøytrale** (true). Certreq-siden sperrer med vilje
 *     ikke på dokumentasjon — søkeren kan gå videre og laste opp senere — og et
 *     alderskrav motoren ikke får vurdert skal aldri kunne låse henne ute.
 *   - `allOf`: alle grenene må være tilfredsstilt. `anyOf`/`oneOf`: minst én.
 *
 * For SID gir det nøyaktig legacy-oppførselen: Alt2 krever begge sine
 * erklæringer, og alternativgruppen krever at minst én vei er fullt erklært.
 * Et kravtre uten erklæringer er alltid tilfredsstilt, så alle andre
 * sertifikater er upåvirket.
 */
export const declarationsSatisfied = (
  result: RequirementResult | null | undefined,
  isConfirmed: (requirementId?: string | null) => boolean
): boolean => {
  if (!result) return true;

  if (isLeaf(result)) {
    if (!isDeclarationRequirement(result.requirementType)) return true;
    return isConfirmed(result.requirementId);
  }

  const allOf = result.allOf || [];
  const anyOf = result.anyOf || [];
  const oneOf = result.oneOf || [];

  if (!allOf.every((child) => declarationsSatisfied(child, isConfirmed))) return false;
  if (anyOf.length > 0 && !anyOf.some((child) => declarationsSatisfied(child, isConfirmed))) {
    return false;
  }
  if (oneOf.length > 0 && !oneOf.some((child) => declarationsSatisfied(child, isConfirmed))) {
    return false;
  }

  return true;
};

/** Finnes det erklæringskrav i treet i det hele tatt? */
export const hasDeclarationRequirements = (
  result: RequirementResult | null | undefined
): boolean => {
  if (!result) return false;
  if (isLeaf(result)) return isDeclarationRequirement(result.requirementType);
  return [...(result.allOf || []), ...(result.anyOf || []), ...(result.oneOf || [])].some(
    hasDeclarationRequirements
  );
};
